import React, { useState, useEffect } from 'react';
import { ArrowLeft, Award, TrendingUp, Globe, Users, Sparkles, Target, Heart, Zap, Star, Crown, Brain, Rocket, ChevronDown, Linkedin, Twitter, Instagram, Package, Droplets } from 'lucide-react';

interface OurStoryProps {
  onBackToWaitlist: () => void;
}

export default function OurStory({ onBackToWaitlist }: OurStoryProps) {
  const [currentMilestone, setCurrentMilestone] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setCurrentMilestone((prev) => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleSocialClick = (platform: string) => {
    switch (platform) {
      case 'linkedin':
        window.open('https://sg.linkedin.com/in/jonaoboza', '_blank');
        break;
      case 'instagram':
        window.open('https://instagram.com/worththesqueeze', '_blank');
        break;
      case 'twitter':
        window.open('https://x.com/worththesqueeze', '_blank');
        break;
    }
  };

  return (
    <>
      <style jsx>{`
        .font-inter { font-family: 'Inter', sans-serif; }
        .font-poppins { font-family: 'Poppins', sans-serif; }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        @keyframes pulse-glow {
          0%, 100% { 
            box-shadow: 0 0 40px rgba(236, 72, 153, 0.4);
          }
          50% { 
            box-shadow: 0 0 60px rgba(236, 72, 153, 0.8);
          }
        }
        
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-40px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(40px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        @keyframes morphBackground {
          0%, 100% { 
            background: linear-gradient(135deg, #1e1b4b, #581c87, #be185d);
          }
          33% { 
            background: linear-gradient(135deg, #581c87, #be185d, #0c4a6e);
          }
          66% { 
            background: linear-gradient(135deg, #be185d, #0c4a6e, #1e1b4b);
          }
        }
        
        @keyframes worthTheSqueezeGlow {
          0%, 100% { 
            color: #ec4899; 
            text-shadow: 0 0 20px rgba(236, 72, 153, 0.8);
          }
          50% { 
            color: #8b5cf6; 
            text-shadow: 0 0 30px rgba(139, 92, 246, 0.9);
          }
        }
        
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-pulse-glow { animation: pulse-glow 3s ease-in-out infinite; }
        .animate-slide-up { animation: slideUp 0.8s ease-out forwards; }
        .animate-slide-in-left { animation: slideInLeft 0.8s ease-out forwards; }
        .animate-slide-in-right { animation: slideInRight 0.8s ease-out forwards; }
        .animate-scale-in { animation: scaleIn 0.8s ease-out forwards; }
        .animate-morph { animation: morphBackground 12s ease-in-out infinite; }
        .animate-worth-glow { animation: worthTheSqueezeGlow 3s ease-in-out infinite; }
        
        .glass-morphism {
          background: rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(24px);
          border: 1px solid rgba(255, 255, 255, 0.12);
        }
        
        .premium-card {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
          backdrop-filter: blur(30px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }
        
        .gradient-text {
          background: linear-gradient(135deg, #ec4899, #8b5cf6, #06b6d4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .hover-lift:hover {
          transform: translateY(-8px);
        }
        
        .founder-photo {
          width: 100%;
          height: 400px;
          object-fit: cover;
          object-position: center top;
          border-radius: 20px;
          filter: contrast(1.15) saturate(1.2) brightness(1.1);
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .founder-photo:hover {
          filter: contrast(1.2) saturate(1.3) brightness(1.15);
          transform: scale(1.02);
        }
        
        .founder-photo-container {
          position: relative;
          overflow: hidden;
          border-radius: 24px;
          background: linear-gradient(135deg, rgba(236, 72, 153, 0.1), rgba(139, 92, 246, 0.1));
        }
        
        .founder-photo-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(236, 72, 153, 0.15), rgba(139, 92, 246, 0.15));
          mix-blend-mode: overlay;
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        
        .founder-photo-container:hover .founder-photo-overlay {
          opacity: 1;
        }
        
        .shimmer-effect {
          position: relative;
          overflow: hidden;
        }
        
        .shimmer-effect::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
          animation: shimmer 3s infinite;
        }
        
        .milestone-indicator {
          transition: all 0.3s ease;
        }
        
        .milestone-indicator.active {
          transform: scale(1.2);
          box-shadow: 0 0 20px rgba(236, 72, 153, 0.6);
        }
        
        .text-reveal {
          opacity: 0;
          transform: translateY(20px);
          transition: all 0.6s ease;
        }
        
        .text-reveal.visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        .social-button {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .social-button:hover {
          transform: translateY(-2px) scale(1.05);
        }
        
        .social-button:active {
          transform: translateY(0) scale(0.95);
        }
      `}</style>

      <div className="min-h-screen animate-morph relative overflow-hidden">
        {/* Enhanced Motion Blur Effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-0 w-full h-1 bg-gradient-to-r from-transparent via-pink-500/30 to-transparent animate-pulse"></div>
          <div className="absolute top-3/4 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-500/30 to-transparent animate-pulse" style={{ animationDelay: '1.5s' }}></div>
        </div>

        {/* Back Button */}
        <button
          onClick={onBackToWaitlist}
          className="fixed top-6 left-6 z-50 glass-morphism text-pink-400 px-6 py-3 rounded-full font-medium hover:bg-pink-500/10 transition-all duration-300 flex items-center space-x-2 hover:scale-105"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Back to Home</span>
        </button>

        {/* Compact Header */}
        <section className="pt-24 pb-12 relative">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <div className="mb-8">
              <img 
                src="/logo transparent with tagline.png" 
                alt="UNDRESSING™ Sauce Logo" 
                className="h-16 w-auto mx-auto mb-6 object-contain animate-float"
              />
            </div>
            
            <h1 className={`font-poppins font-black text-4xl sm:text-5xl lg:text-6xl text-white mb-4 text-reveal ${isVisible ? 'visible' : ''}`}>
              Our
              <span className="gradient-text block">Story</span>
            </h1>
            
            <p className={`font-inter text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed text-reveal ${isVisible ? 'visible' : ''}`} style={{ transitionDelay: '0.2s' }}>
              From award-winning digital innovation to flavor revolution—every bold venture is <span className="animate-worth-glow font-bold">worth the squeeze</span>
            </p>
            
            {/* Scroll Indicator */}
            <div className="mt-8 animate-bounce">
              <ChevronDown className="h-6 w-6 text-pink-400 mx-auto" />
            </div>
          </div>
        </section>

        {/* Meet Our Founder Section - Enhanced */}
        <section className="py-16 relative">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-poppins font-black text-3xl sm:text-4xl lg:text-5xl text-white mb-4">
                Meet Our Founder
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-pink-500 to-purple-600 mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Left Column - Founder Image */}
              <div className="relative animate-slide-in-left">
                <div className="premium-card rounded-3xl p-6 hover-lift transition-all duration-500 shimmer-effect">
                  {/* Founder Photo */}
                  <div className="founder-photo-container mb-6">
                    <img 
                      src="/Jona Oboza BIO Photo.jpeg"
                      alt="Jona Oboza - Founder & CEO of UNDRESSING™ Sauce"
                      className="founder-photo"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const fallback = target.nextElementSibling as HTMLElement;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                    {/* Fallback placeholder */}
                    <div className="w-full h-96 bg-gradient-to-br from-pink-500/20 to-purple-600/20 rounded-2xl flex items-center justify-center" style={{ display: 'none' }}>
                      <div className="text-center">
                        <div className="w-32 h-32 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse-glow">
                          <Crown className="h-16 w-16 text-white" />
                        </div>
                        <p className="text-gray-400 text-sm">Jona Oboza</p>
                        <p className="text-gray-500 text-xs">Founder & CEO</p>
                      </div>
                    </div>
                    <div className="founder-photo-overlay"></div>
                  </div>
                  
                  {/* Social Links */}
                  <div className="flex justify-center space-x-4">
                    <button 
                      onClick={() => handleSocialClick('linkedin')}
                      className="social-button w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-full flex items-center justify-center shadow-lg"
                      title="LinkedIn - Jona Oboza"
                    >
                      <Linkedin className="h-6 w-6 text-white" />
                    </button>
                    <button 
                      onClick={() => handleSocialClick('instagram')}
                      className="social-button w-12 h-12 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg"
                      title="Instagram - @worththesqueeze"
                    >
                      <Instagram className="h-6 w-6 text-white" />
                    </button>
                    <button 
                      onClick={() => handleSocialClick('twitter')}
                      className="social-button w-12 h-12 bg-gradient-to-r from-gray-800 to-black rounded-full flex items-center justify-center shadow-lg"
                      title="X (Twitter) - @worththesqueeze"
                    >
                      <Twitter className="h-6 w-6 text-white" />
                    </button>
                  </div>
                  
                  {/* Floating Achievement Icons */}
                  <div className="absolute -top-4 -right-4 w-16 h-16 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center animate-float shadow-lg">
                    <Award className="h-8 w-8 text-white" />
                  </div>
                  
                  <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center animate-float shadow-lg" style={{ animationDelay: '2s' }}>
                    <TrendingUp className="h-8 w-8 text-white" />
                  </div>
                </div>
              </div>

              {/* Right Column - Founder Bio */}
              <div className="space-y-6 animate-slide-in-right">
                <div>
                  <h3 className="font-poppins font-bold text-3xl sm:text-4xl text-white mb-2">
                    Jona Oboza
                  </h3>
                  <p className="text-pink-400 font-bold text-2xl sm:text-3xl mb-2 animate-worth-glow">
                    @worththesqueeze
                  </p>
                  <p className="text-slate-300 font-semibold text-lg sm:text-xl mb-4">
                    Founder & CEO • Flavor Revolutionary
                  </p>
                </div>

                <div className="space-y-4 text-slate-300 leading-relaxed">
                  <p className="font-inter text-base sm:text-lg">
                    Award-winning former regional ExCo leader turned flavor innovator. After leading globally acclaimed teams at <strong className="text-white">GroupM, Xaxis, PubMatic, Publicis, and Omnicom</strong>, Jona discovered that the most transformative experiences are always <span className="text-pink-400 font-bold">worth the squeeze</span>.
                  </p>
                  
                  <p className="font-inter text-base sm:text-lg">
                    Recognized by <strong className="text-white">Campaign Asia's 40 Under 40</strong>, <strong className="text-white">Young Business Leader of the Year</strong>, and <strong className="text-white">Programmatic Person of the Year</strong>—but her greatest achievement? Realizing that bold flavors, like bold ideas, require courage to extract their full potential.
                  </p>
                  
                  <p className="font-inter text-base sm:text-lg">
                    As founder of <strong className="text-cyan-400">Acceler8 AI</strong> (bootstrapped to <strong className="text-green-400">$700K revenue in 7 months</strong>), she proved that innovation happens when you're willing to squeeze every drop of possibility from an idea. Now, with UNDRESSING™ Sauce, Jona is applying that same relentless pursuit of excellence to revolutionize how we experience flavor.
                  </p>
                  
                  <div className="bg-gradient-to-r from-pink-500/10 to-purple-600/10 border border-pink-500/20 rounded-xl p-6">
                    <p className="font-inter text-base sm:text-lg text-pink-200 italic leading-relaxed">
                      "Every UNDRESSING™ Sauce pouch is a moment of pure transformation—stripping away the ordinary to reveal extraordinary flavor that fuels your body, mind, and spirit. Each squeeze delivers not just bold taste, but the confidence to live boldly. Because when you dare to strip the bland and drip the bold, you discover what makes life truly <span className="text-pink-400 font-bold">worth the squeeze</span>."
                    </p>
                  </div>
                </div>

                {/* Achievement Badges - Compact Grid */}
                <div className="grid grid-cols-2 gap-3 mt-6">
                  {[
                    { icon: Award, title: '40 Under 40', desc: 'Campaign Asia', color: 'from-yellow-500 to-orange-500' },
                    { icon: Crown, title: 'Young Leader', desc: 'of the Year', color: 'from-purple-500 to-pink-500' },
                    { icon: Star, title: 'Programmatic', desc: 'Person of Year', color: 'from-blue-500 to-cyan-500' },
                    { icon: TrendingUp, title: '$700K Revenue', desc: 'in 7 months', color: 'from-green-500 to-emerald-500' }
                  ].map((achievement, index) => (
                    <div key={index} className="glass-morphism rounded-xl p-3 text-center hover-lift transition-all duration-300 group">
                      <div className={`w-8 h-8 bg-gradient-to-r ${achievement.color} rounded-lg flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform`}>
                        <achievement.icon className="h-4 w-4 text-white" />
                      </div>
                      <h4 className="text-white font-semibold text-xs">{achievement.title}</h4>
                      <p className="text-gray-400 text-xs">{achievement.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Vision & Mission - Side by Side */}
        <section className="py-16 relative">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="premium-card rounded-2xl p-8 hover-lift transition-all duration-300 animate-scale-in">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-purple-600 rounded-xl flex items-center justify-center">
                    <Target className="h-6 w-6 text-white" />
                  </div>
                  <h2 className="font-poppins font-bold text-2xl sm:text-3xl text-white">Our Vision</h2>
                </div>
                <p className="font-inter text-base sm:text-lg text-slate-300 leading-relaxed">
                  To create a world where every meal becomes a moment of transformation. Where bold flavors meet functional wellness, and convenience never compromises quality. Because extraordinary experiences are always <span className="text-pink-400 font-semibold">worth the squeeze</span>.
                </p>
              </div>

              <div className="premium-card rounded-2xl p-8 hover-lift transition-all duration-300 animate-scale-in" style={{ animationDelay: '0.2s' }}>
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center">
                    <Rocket className="h-6 w-6 text-white" />
                  </div>
                  <h2 className="font-poppins font-bold text-2xl sm:text-3xl text-white">Our Mission</h2>
                </div>
                <p className="font-inter text-base sm:text-lg text-slate-300 leading-relaxed">
                  Ditch the bland, drip the bold, glow all day. We're redefining flavor with cutting-edge sauce tech that elevates both taste and wellness, because excellence should always come with a squeeze of brilliance.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Company Values - Compact Grid */}
        <section className="py-16 relative">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-poppins font-black text-3xl sm:text-4xl lg:text-5xl text-white mb-4">
                Our Values
              </h2>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  icon: Brain,
                  title: 'Innovation',
                  color: 'from-purple-500 to-indigo-500'
                },
                {
                  icon: Heart,
                  title: 'Wellness',
                  color: 'from-pink-500 to-rose-500'
                },
                {
                  icon: Globe,
                  title: 'Sustainability',
                  color: 'from-green-500 to-emerald-500'
                },
                {
                  icon: Users,
                  title: 'Community',
                  color: 'from-cyan-500 to-blue-500'
                }
              ].map((value, index) => (
                <div key={index} className="premium-card rounded-xl p-6 text-center hover-lift transition-all duration-300 animate-slide-up" style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className={`w-12 h-12 bg-gradient-to-r ${value.color} rounded-xl flex items-center justify-center mx-auto mb-4`}>
                    <value.icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="font-poppins font-bold text-lg text-white">{value.title}</h3>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* The Journey - Interactive Timeline */}
        <section className="py-16 relative">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-poppins font-black text-3xl sm:text-4xl lg:text-5xl text-white mb-4">
                The Journey
              </h2>
            </div>

            {/* Timeline Indicators */}
            <div className="flex justify-center space-x-4 mb-12">
              {[0, 1, 2].map((index) => (
                <button
                  key={index}
                  onClick={() => setCurrentMilestone(index)}
                  className={`milestone-indicator w-4 h-4 rounded-full transition-all duration-300 ${
                    index === currentMilestone 
                      ? 'bg-gradient-to-r from-pink-500 to-purple-600 active' 
                      : 'bg-white/30 hover:bg-white/50'
                  }`}
                />
              ))}
            </div>

            {/* Timeline Content */}
            <div className="space-y-8">
              {[
                {
                  year: '2020-2024',
                  title: 'Digital Media Mastery',
                  icon: Award,
                  color: 'from-yellow-500 to-orange-500'
                },
                {
                  year: '2024',
                  title: 'Acceler8 AI Revolution',
                  icon: Rocket,
                  color: 'from-blue-500 to-cyan-500'
                },
                {
                  year: '2025',
                  title: 'UNDRESSING™ Sauce Launch',
                  icon: Sparkles,
                  color: 'from-pink-500 to-purple-600'
                }
              ].map((milestone, index) => (
                <div 
                  key={index} 
                  className={`transition-all duration-500 ${
                    index === currentMilestone ? 'opacity-100 scale-100' : 'opacity-50 scale-95'
                  }`}
                >
                  <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-8">
                    <div className={`w-20 h-20 bg-gradient-to-r ${milestone.color} rounded-full flex items-center justify-center flex-shrink-0 animate-pulse-glow`}>
                      <milestone.icon className="h-10 w-10 text-white" />
                    </div>
                    <div className="premium-card rounded-xl p-6 flex-1 text-center md:text-left">
                      <div className="flex flex-col md:flex-row md:items-center md:space-x-4 mb-3">
                        <span className="text-pink-400 font-bold text-lg">{milestone.year}</span>
                        <h3 className="font-poppins font-bold text-xl sm:text-2xl text-white">{milestone.title}</h3>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Call to Action - Compact */}
        <section className="py-16 relative">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <div className="premium-card rounded-2xl p-12 shimmer-effect">
              <h2 className="font-poppins font-black text-3xl sm:text-4xl text-white mb-6">
                Join the Flavor
                <span className="gradient-text block">Revolution</span>
              </h2>
              <p className="font-inter text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
                Be part of the journey as we prove that bold flavors, innovative wellness, and sustainable choices are always <span className="text-pink-400 font-bold animate-worth-glow">worth the squeeze</span>.
              </p>
              <button 
                onClick={onBackToWaitlist}
                className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-10 py-4 font-poppins font-bold text-lg hover:shadow-2xl hover:shadow-pink-500/25 transition-all duration-300 hover:scale-105 rounded-xl"
              >
                Join the Waitlist
              </button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}